import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, ActionBtn, SectionHeader, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete, MdCheckCircle } from '../utils/icons';
import { FaFileInvoiceDollar } from 'react-icons/fa';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const initialForm = { title: '', creditor: '', amount: '', date: '', dueDate: '', description: '' };

const DEBT_STATUS = {
  Unpaid: { label: 'پرداخت‌نشده', color: 'bg-red-100 text-red-700' },
  PartiallyPaid: { label: 'پرداخت جزئی', color: 'bg-yellow-100 text-yellow-700' },
  Paid: { label: 'پرداخت‌شده', color: 'bg-green-100 text-green-700' },
};

export default function OfficeDebts() {
  const { isManager } = useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [payItem, setPayItem] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      const { data } = await API.get('/office-debts', { params });
      setItems(data.data);
    } catch (err) {
      toast.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filterStatus]);

  const openAdd = () => { setForm({ ...initialForm, date: new Date() }); setEditItem(null); setShowModal(true); };
  const openEdit = (item) => {
    setForm({
      title: item.title, creditor: item.creditor, amount: item.amount,
      date: item.date || '', dueDate: item.dueDate || '', description: item.description || ''
    });
    setEditItem(item);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.creditor || !form.amount) {
      return toast.error('عنوان، طلبکار و مبلغ ضروری است');
    }
    setSaving(true);
    try {
      if (editItem) {
        await API.put(`/office-debts/${editItem._id}`, form);
        toast.success('بروزرسانی شد');
      } else {
        await API.post('/office-debts', form);
        toast.success('بدهکاری ثبت شد');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/office-debts/${deleteId}`);
      toast.success('حذف شد');
      setDeleteId(null);
      load();
    } catch (err) {
      toast.error('خطا در حذف');
    }
  };

  const openPay = (item) => {
    const remaining = item.amount - (item.paidAmount || 0);
    setPayAmount(remaining);
    setPayItem(item);
  };

  const submitPayment = async () => {
    const remaining = payItem.amount - (payItem.paidAmount || 0);
    if (!payAmount || payAmount <= 0) return toast.error('مبلغ پرداخت را وارد کنید');
    if (Number(payAmount) > remaining) return toast.error(`مبلغ نباید از باقی‌مانده (${formatCurrency(remaining)}) بیشتر باشد`);
    try {
      await API.patch(`/office-debts/${payItem._id}/pay`, { amount: Number(payAmount) });
      toast.success('پرداخت ثبت شد');
      setPayItem(null);
      setPayAmount('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    }
  };

  if (loading) return <PageLoader />;

  const totalUnpaid = items.filter(i => i.status !== 'Paid').reduce((s, i) => s + (i.amount - (i.paidAmount || 0)), 0);
  const totalPaid = items.reduce((s, i) => s + (i.paidAmount || 0), 0);
  const unpaidCount = items.filter(i => i.status !== 'Paid').length;

  const PAGE_SIZE = 10;
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="بدهکاری‌های دفتر"
        subtitle="مبالغی که دفتر به دیگران بدهکار است"
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="بدهکاری جدید" />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={<FaFileInvoiceDollar className="text-red-600" />} label="مجموع پرداخت‌نشده" value={formatCurrency(totalUnpaid)} sub={`${unpaidCount} مورد`} color="red" />
        <StatCard icon={<FaFileInvoiceDollar className="text-green-600" />} label="مجموع پرداخت‌شده" value={formatCurrency(totalPaid)} color="green" />
        <StatCard icon={<FaFileInvoiceDollar className="text-blue-600" />} label="تعداد کل" value={items.length} color="blue" />
      </div>

      <div className="glass-card p-4">
        <select className="form-input w-full sm:w-48" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">همه</option>
          <option value="Unpaid">پرداخت‌نشده</option>
          <option value="Paid">پرداخت‌شده</option>
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState icon="🧾" title="بدهکاری‌ای ثبت نشده" action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="بدهکاری جدید" />} />
      ) : (
        <>
          <div className="hidden md:block glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr>
                    <th>عنوان</th>
                    <th>طلبکار</th>
                    <th>مبلغ</th>
                    <th>تاریخ</th>
                    <th>سررسید</th>
                    <th>وضعیت</th>
                    <th>عملیات</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map(item => (
                    <tr key={item._id}>
                      <td className="font-medium text-gray-800">{item.title}</td>
                      <td className="text-gray-600">{item.creditor}</td>
                      <td className="text-gray-800">
                        {formatCurrency(item.amount)}
                        {item.paidAmount > 0 && (
                          <div className="text-xs text-gray-500">باقی‌مانده: {formatCurrency(item.amount - item.paidAmount)}</div>
                        )}
                      </td>
                      <td className="text-gray-500">{toShamsi(item.date)}</td>
                      <td className="text-gray-500">{item.dueDate ? toShamsi(item.dueDate) : '---'}</td>
                      <td>
                        <span className={`badge ${DEBT_STATUS[item.status]?.color}`}>
                          {DEBT_STATUS[item.status]?.label}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          {isManager && item.status !== 'Paid' && (
                            <button onClick={() => openPay(item)} className="p-1.5 text-green-600 hover:text-green-700 hover:bg-green-100 rounded-lg transition-all" title="پرداخت">
                              <MdCheckCircle size={16} />
                            </button>
                          )}
                          {isManager && (
                            <>
                              <button onClick={() => openEdit(item)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all" title="ویرایش">
                                <MdEdit size={16} />
                              </button>
                              <button onClick={() => setDeleteId(item._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all" title="حذف">
                                <MdDelete size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="md:hidden space-y-3">
            {paged.map(item => (
              <div key={item._id} className="mobile-card">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-medium text-gray-800">{item.title}</span>
                  <span className={`badge ${DEBT_STATUS[item.status]?.color}`}>
                    {DEBT_STATUS[item.status]?.label}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-1">طلبکار: {item.creditor}</div>
                <div className="text-red-600 font-bold mb-2">
                  {formatCurrency(item.amount)}
                  {item.paidAmount > 0 && <span className="text-xs text-gray-500 font-normal"> • باقی‌مانده: {formatCurrency(item.amount - item.paidAmount)}</span>}
                </div>
                <div className="text-xs text-gray-500 mb-2">{toShamsi(item.date)}{item.dueDate && ` • سررسید: ${toShamsi(item.dueDate)}`}</div>
                {isManager && (
                  <div className="flex gap-2">
                    {item.status !== 'Paid' && (
                      <button onClick={() => openPay(item)} className="flex-1 py-1.5 text-xs bg-green-100 text-green-600 rounded-lg">پرداخت</button>
                    )}
                    <button onClick={() => openEdit(item)} className="flex-1 py-1.5 text-xs bg-yellow-50 text-yellow-700 rounded-lg">ویرایش</button>
                    <button onClick={() => setDeleteId(item._id)} className="flex-1 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg">حذف</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* مودال اضافه/ویرایش */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش بدهکاری' : 'بدهکاری جدید'} size="md">
        <div className="space-y-4">
          <FormField label="عنوان" required>
            <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="مثلاً: قرض خرید تجهیزات" />
          </FormField>
          <FormField label="طلبکار (به چه کسی بدهکاریم)" required>
            <input className="form-input" value={form.creditor} onChange={e => setForm({ ...form, creditor: e.target.value })} placeholder="نام شخص یا شرکت" />
          </FormField>
          <FormField label="مبلغ (افغانی)" required>
            <input type="number" className="form-input" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="تاریخ">
              <ShamsiDatePicker value={form.date} onChange={date => setForm({ ...form, date })} className="form-input" />
            </FormField>
            <FormField label="سررسید (اختیاری)">
              <ShamsiDatePicker value={form.dueDate} onChange={date => setForm({ ...form, dueDate: date })} className="form-input" />
            </FormField>
          </div>
          <FormField label="توضیحات">
            <textarea className="form-input h-20" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="توضیحات (اختیاری)" />
          </FormField>
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm">انصراف</button>
            <ActionBtn onClick={handleSave} label={saving ? 'در حال ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
          </div>
        </div>
      </Modal>

      {/* تأیید علامت‌گذاری پرداخت */}
      {payItem && (
        <Modal isOpen={!!payItem} onClose={() => setPayItem(null)} title={`ثبت پرداخت - ${payItem.title}`} size="sm">
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">مبلغ کل:</span><span className="text-gray-800">{formatCurrency(payItem.amount)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">پرداخت‌شده تاکنون:</span><span className="text-blue-600">{formatCurrency(payItem.paidAmount || 0)}</span></div>
              <div className="flex justify-between font-bold"><span className="text-gray-600">باقی‌مانده:</span><span className="text-red-600">{formatCurrency(payItem.amount - (payItem.paidAmount || 0))}</span></div>
            </div>
            <FormField label="مبلغ پرداختی الان" required>
              <input
                type="number"
                className="form-input"
                value={payAmount}
                onChange={e => setPayAmount(e.target.value)}
                max={payItem.amount - (payItem.paidAmount || 0)}
                placeholder="0"
              />
            </FormField>
            <p className="text-xs text-gray-500">می‌توانید کمتر از کل مبلغ را پرداخت کنید؛ باقی‌مانده برای پرداخت بعدی می‌ماند. فقط همین مبلغ از سود خالص کم می‌شود.</p>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setPayItem(null)} className="px-4 py-2 rounded-xl bg-gray-100 text-gray-800 text-sm">انصراف</button>
              <ActionBtn onClick={submitPayment} label="ثبت پرداخت" />
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="حذف بدهکاری"
        message="آیا از حذف این رکورد مطمئن هستید؟"
      />
    </div>
  );
}
